import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Wallet, Package, Award, Rocket, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, formatDate, errText, imgUrl, PAYOUT_STATUS } from "../lib/api";
import { BadgeList } from "../components/BadgeChip";

function MyItemsSection() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/my/items").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (it, status, msg) => {
    try {
      await api.patch(`/items/${it.id}/status`, { status });
      toast.success(msg);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const STATUS_LABEL = { ACTIVE: "Online", HIDDEN: "Pausiert", DRAFT: "Entwurf", SOLD: "Verkauft", RESERVED: "Reserviert", BLOCKED: "Gesperrt" };
  if (!items) return null;
  return (
    <div className="mt-10" data-testid="my-items-section">
      <h2 className="text-base font-bold mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-accent" /> Meine Artikel ({items.length})</h2>
      {items.length === 0 ? (
        <div className="border border-border rounded-2xl p-6 text-sm text-muted-foreground">Du hast noch keine Artikel eingestellt. <Link to="/verkaufen" className="text-accent font-bold hover:underline">Jetzt ersten Artikel einstellen</Link></div>
      ) : (
        <div className="border border-border rounded-2xl divide-y divide-border overflow-hidden bg-white">
          {items.map((it) => (
            <div key={it.id} className="p-3.5 flex items-center gap-3.5" data-testid={`my-item-row-${it.id}`}>
              <Link to={`/artikel/${it.id}`} className="shrink-0">
                {it.image_ids?.length ? (
                  <img src={imgUrl(it.image_ids[0], "thumb")} alt={it.title} className="w-14 h-14 rounded-xl object-cover border border-border" />
                ) : <span className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center"><Package className="w-5 h-5 text-muted-foreground" /></span>}
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={`/artikel/${it.id}`} className="text-sm font-bold truncate block hover:text-accent">{it.title}</Link>
                <p className="text-xs text-muted-foreground mt-0.5">{formatPrice(it.price)} · <span className={`font-semibold ${it.status === "ACTIVE" ? "text-green-600" : it.status === "SOLD" ? "text-accent" : "text-amber-600"}`}>{STATUS_LABEL[it.status] || it.status}</span></p>
              </div>
              {["ACTIVE", "HIDDEN", "DRAFT"].includes(it.status) && (
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`toggle-status-${it.id}`} onClick={() => setStatus(it, it.status === "ACTIVE" ? "HIDDEN" : "ACTIVE", it.status === "ACTIVE" ? "Artikel pausiert" : "Artikel ist wieder online")}
                    className="text-xs font-bold border border-border rounded-full px-3.5 py-2 hover:bg-secondary">{it.status === "ACTIVE" ? "Pausieren" : "Aktivieren"}</button>
                  <button data-testid={`delete-item-${it.id}`} onClick={() => window.confirm(`„${it.title}" endgültig löschen?`) && setStatus(it, "DELETED", "Artikel gelöscht")}
                    className="text-xs font-bold border border-red-300 text-red-600 rounded-full px-3.5 py-2 hover:bg-red-50">Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BadgeSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/seller/badges").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="mt-10" data-testid="my-badges-section">
      <h2 className="text-base font-bold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-accent" /> Meine Abzeichen</h2>
      {data.earned.length > 0 ? (
        <div className="mb-4"><BadgeList badges={data.earned} max={6} size="md" /></div>
      ) : (
        <p className="text-sm text-muted-foreground mb-4">Noch keine Abzeichen — verkaufe zuverlässig, antworte schnell und sammle gute Bewertungen.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {data.progress.map((p) => (
          <div key={p.key} className="border border-border rounded-2xl p-4 bg-card" data-testid={`badge-progress-${p.key}`}>
            <p className="font-bold text-sm">{p.name}</p>
            <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
            <div className="space-y-2">
              {p.items.map((it) => {
                const pct = it.type === "bool" ? (it.current ? 100 : 0) : Math.min(100, Math.round((it.current / it.target) * 100));
                return (
                  <div key={it.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span>{it.label}</span>
                      <span className="font-semibold">{it.type === "bool" ? (it.current ? "✓" : "–") : `${it.current} / ${it.target}${it.type === "percent" ? " %" : ""}`}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 100 ? "bg-accent" : "bg-foreground/60"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayoutStatusBanner({ startOnboarding, busy }) {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/seller/status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  if (!st) return null;
  const STATE_LABEL = {
    NOT_SET_UP: "Nicht eingerichtet", ONBOARDING_REQUIRED: "Onboarding erforderlich", IN_REVIEW: "In Prüfung",
    RESTRICTED: "Eingeschränkt", PAYOUTS_ENABLED: "Auszahlungsfähig", UNKNOWN: "Status wird geprüft",
  };
  const ready = st.payout_state === "PAYOUTS_ENABLED";
  return (
    <div className="space-y-3">
      <div className={`border rounded-2xl p-4 ${ready ? "border-green-300 bg-green-50/50" : "border-amber-300 bg-amber-50/60"}`} data-testid="payout-status-banner">
        <p className="text-sm font-bold">Stripe Connect · <span data-testid="payout-state-label">{STATE_LABEL[st.payout_state] || st.payout_state}</span></p>
        {ready ? (
          <p className="text-xs text-muted-foreground mt-1">✓ Konto verbunden · ✓ Verifizierung abgeschlossen · ✓ Auszahlungen aktiviert</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mt-1">
              {st.payout_state === "NOT_SET_UP" && "Verbinde dein Stripe-Konto, um deine Verkaufserlöse auszahlen zu lassen."}
              {st.payout_state === "ONBOARDING_REQUIRED" && "Schließe dein Stripe-Onboarding ab, um Verkaufserlöse auszahlen zu können."}
              {st.payout_state === "IN_REVIEW" && "Stripe prüft deine Angaben. Das dauert in der Regel nur kurz."}
              {st.payout_state === "RESTRICTED" && `Stripe benötigt weitere Angaben${st.requirements_due ? ` (${st.requirements_due} offen)` : ""}. Bitte vervollständige deine Verifizierung.`}
              {st.payout_state === "UNKNOWN" && "Der Status konnte gerade nicht abgerufen werden. Bitte später erneut prüfen."}
            </p>
            {["NOT_SET_UP", "ONBOARDING_REQUIRED", "RESTRICTED"].includes(st.payout_state) && (
              <button data-testid="payout-banner-onboard-btn" onClick={startOnboarding} disabled={busy}
                className="mt-2.5 bg-foreground text-white text-xs font-bold px-4 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50">Stripe Connect einrichten</button>
            )}
          </>
        )}
      </div>
      {st.awaiting_verification_count > 0 && !ready && (
        <div className="border border-accent/40 bg-accent/5 rounded-2xl p-4" data-testid="awaiting-payout-banner">
          <p className="text-sm font-bold">Du hast offene Verkaufserlöse: {formatPrice(st.awaiting_verification_amount)} ({st.awaiting_verification_count} {st.awaiting_verification_count === 1 ? "Verkauf" : "Verkäufe"})</p>
          <p className="text-xs text-muted-foreground mt-1">Auszahlung wartet auf Stripe-Verifizierung. Schließe deine Verifizierung ab, damit die Auszahlung verarbeitet werden kann.</p>
          <button data-testid="awaiting-payout-onboard-btn" onClick={startOnboarding} disabled={busy}
            className="mt-2.5 bg-accent text-white text-xs font-bold px-4 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50">Stripe-Verifizierung abschließen</button>
        </div>
      )}
    </div>
  );
}

export default function SellerDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = useCallback(() => {
    api.get("/seller/status").catch(() => {});
    api.get("/seller/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  useEffect(() => {
    if (params.get("onboarding") === "done") toast.success("Onboarding-Daten übermittelt — Prüfung läuft");
    load();
  }, [load, params]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seller/onboard", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };

  if (data === null) return <div className="max-w-4xl mx-auto px-4 py-10"><div className="h-64 bg-muted rounded-3xl animate-pulse" /></div>;
  if (data === false) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-muted-foreground">Fehler beim Laden.</div>;

  const s = data.seller || {};
  const kycDone = s.payouts_enabled;

  return (
    <div className="bg-[#FAF9F5] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-20">
        <h1 className="font-display text-2xl sm:text-4xl font-bold">Verkäuferbereich</h1>

        <Link to="/geldbeutel" data-testid="wallet-link-card"
          className="card-lift relative overflow-hidden mt-5 rounded-3xl p-6 md:p-7 bg-[#111111] text-white flex items-center gap-5 group block">
          <div aria-hidden="true" className="absolute -top-16 -right-16 w-[260px] h-[260px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
          <span className="relative w-12 h-12 rounded-2xl bg-accent/20 text-accent flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"><Wallet className="w-6 h-6" /></span>
          <span className="relative flex-1 min-w-0">
            <span className="block font-display text-lg md:text-xl font-bold">Dein Geldbeutel</span>
            <span className="block text-xs md:text-sm text-white/60 mt-0.5">Guthaben, Umsatz, letzte Verkäufe & Auszahlungen — alles auf einen Blick</span>
          </span>
          <span className="relative w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 transition-transform group-hover:translate-x-1"><ChevronRight className="w-5 h-5" /></span>
        </Link>

        <div className="mt-5 space-y-4">
          <PayoutStatusBanner startOnboarding={startOnboarding} busy={busy} />

          {!kycDone && (
            <div className="rounded-2xl p-5 border flex flex-wrap items-center gap-4 bg-amber-50 border-amber-300" data-testid="kyc-status-card">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-sm">{s.onboarding_status === "IN_PROGRESS" || s.onboarding_status === "COMPLETED" ? "Verifizierung in Prüfung" : "Verifizierung erforderlich"}</p>
                <p className="text-xs text-muted-foreground mt-1">Für Auszahlungen musst du das Stripe-Onboarding (Identität + Bankkonto) abschließen. Deine Verkäufe bleiben solange sicher reserviert.</p>
              </div>
              <button data-testid="start-onboarding-btn" onClick={startOnboarding} disabled={busy}
                className="bg-primary text-primary-foreground text-sm font-bold px-6 py-3 rounded-full hover:opacity-90 disabled:opacity-50">
                {busy ? "Weiterleitung…" : s.stripe_account_id ? "Onboarding fortsetzen" : "Jetzt verifizieren"}
              </button>
            </div>
          )}
        </div>

        <Link to="/werbung" data-testid="seller-ads-link"
          className="card-lift mt-5 rounded-2xl p-5 border border-[#E5E3DA] bg-white flex items-center gap-4 hover:border-accent/40 group">
          <span className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center shrink-0"><Rocket className="w-5 h-5" /></span>
          <span className="flex-1">
            <span className="block text-sm font-bold group-hover:text-accent transition-colors">Werbung: Artikel boosten & Shop bewerben</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Mehr Sichtbarkeit für deine Artikel — ab 1,99 €</span>
          </span>
        </Link>

        <MyItemsSection />

        <BadgeSection />
      </div>
    </div>
  );
}
